import React, { useEffect, useState } from 'react';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones';
import { 
    ConnectWebSocketForBriefcaseAdmin, 
    DisconnectWebSocketForBriefcaseAdmin,
    ShowBriefcaseFrontPage, 
    StartBriefcaseGame, 
    OpenTargetBriefcase, 
    ResetBriefcaseGame,
    ContinueBriefcaseGame 
} from './../../../utilities/BL/Briefcase';


const Briefcase = ({ getOutOfTheDynamic }) => {
    // ==========================================================================
    // ESTADOS DE CONFIGURACIÓN DEL JUEGO
    // ==========================================================================
    const [totalBriefcasesQuantity, setTotalBriefcasesQuantity] = useState(9);
    const [suspenseDurationMilliseconds, setSuspenseDurationMilliseconds] = useState(2000);
    const [selectedGameMode, setSelectedGameMode] = useState(2); // 0: Aleatorio, 1: Solo Premio Mayor, 2: Varios Premios
    const [winningAttemptNumber, setWinningAttemptNumber] = useState(9);
    const [specialPrizeText, setSpecialPrizeText] = useState("BOLETOS PARA EXPO HATS"); // UN PREMIO
    // const [prizesListString, setPrizesListString] = useState("UN STICKER,,UN PÓSTER,UNA GORRA,,ENTRADAS VIP,UN BALÓN,,UN CUPÓN");
    // const [prizesListString, setPrizesListString] = useState("UNAS CALCETAS,UN JERSEY,UN LLAVERO,UN STICKER,UN PÓSTER,UNA GORRA,,ENTRADAS VIP,UN BALÓN,,UN CUPÓN");
    const [prizesListString, setPrizesListString] = useState("BOLETOS PARA EXPO HATS,,,BOLETOS PARA EXPO HATS,,BOLETOS PARA EXPO HATS,,,BOLETOS EXPO HATS");

    // ==========================================================================
    // ESTADOS DE PARTIDA Y COMUNICACIÓN
    // ==========================================================================
    const [matchPhase, setMatchPhase] = useState('WAITING');
    const [isSystemWorking, setIsSystemWorking] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [websocketStatus, setWebsocketStatus] = useState('idle');

    // Analíticos en vivo
    const [liveBriefcasesState, setLiveBriefcasesState] = useState([]);
    const [userSelectedBriefcaseId, setUserSelectedBriefcaseId] = useState(null);
    const [targetedBriefcaseId, setTargetedBriefcaseId] = useState(null);
    const [totalOpenedBriefcases, setTotalOpenedBriefcases] = useState(0);

    // ==========================================================================
    // MANEJADOR DE WEBSOCKETS
    // ==========================================================================
    const handleIncomingWebsocketMessage = (incomingMessage) => {
        const messageType = (incomingMessage?.type || '').toLowerCase();
        const payloadData = incomingMessage?.payload || {};

        if (messageType === 'briefcase.update') {
            setMatchPhase(payloadData.phase || 'WAITING');
            setLiveBriefcasesState(payloadData.briefcases || []);
            setUserSelectedBriefcaseId(payloadData.userSelectedBriefcaseId || null);
            setTargetedBriefcaseId(payloadData.targetedBriefcaseId || null);
            setTotalOpenedBriefcases(payloadData.openedCount || 0);
        } else if (messageType === 'briefcase.reset') {
            setMatchPhase('WAITING');
            setLiveBriefcasesState([]);
            setUserSelectedBriefcaseId(null);
            setTargetedBriefcaseId(null);
            setTotalOpenedBriefcases(0);
        }
    };

    useEffect(() => {
        ConnectWebSocketForBriefcaseAdmin(handleIncomingWebsocketMessage, setWebsocketStatus);
        return () => {
            DisconnectWebSocketForBriefcaseAdmin();
        };
    }, []);

    // Limpia mensajes temporales automáticamente
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    // ==========================================================================
    // ACCIONES DEL ADMIN
    // ==========================================================================
    const handleShowFrontPage = async () => {
        try {
            setIsSystemWorking(true);
            setErrorMessage(null);
            const response = await ShowBriefcaseFrontPage();
            if (!response.success) setErrorMessage(response.message);
        } catch (error) {
            setErrorMessage("Error al intentar mostrar la portada.");
        } finally {
            setIsSystemWorking(false);
        }
    };

    const handleStartGame = async () => {
        try {
            setIsSystemWorking(true);
            setErrorMessage(null);

            const configurationData = {
                totalBriefcases: totalBriefcasesQuantity,
                suspenseDurationMs: suspenseDurationMilliseconds,
                gameMode: selectedGameMode,
                winningAttempt: winningAttemptNumber,
                specialPrize: specialPrizeText,
                prizesListString: prizesListString
            };

            const response = await StartBriefcaseGame(configurationData);

            if (!response.success) {
                setErrorMessage(response.message);
            } else {
                setSuccessMessage("¡Juego iniciado! Esperando a que el aficionado seleccione su portafolio principal.");
            }
        } catch (exception) {
            setErrorMessage("Ocurrió un error inesperado al iniciar el juego.");
        } finally {
            setIsSystemWorking(false);
        }
    };

    const handleOpenSpecificBriefcase = async (briefcaseIdentifier) => {
        try {
            setIsSystemWorking(true);
            setErrorMessage(null);

            const response = await OpenTargetBriefcase(briefcaseIdentifier);
            if (!response.success) {
                setErrorMessage(response.message);
            }
        } catch (exception) {
            setErrorMessage("Error inesperado al intentar abrir el maletín.");
        } finally {
            setIsSystemWorking(false);
        }
    };

    const handleContinueGame = async () => {
        try {
            setIsSystemWorking(true);
            setErrorMessage(null);
            const response = await ContinueBriefcaseGame();
            if (!response.success) setErrorMessage(response.message);
        } catch (exception) {
            setErrorMessage("Error al intentar continuar.");
        } finally {
            setIsSystemWorking(false);
        }
    };

    const handleAbortAndResetGame = async () => {
        try {
            setIsSystemWorking(true);
            setErrorMessage(null);
            const response = await ResetBriefcaseGame();
            if (!response.success) {
                setErrorMessage(response.message);
            }
        } catch (exception) {
            setErrorMessage("Ocurrió un error inesperado al abortar.");
        } finally {
            setIsSystemWorking(false);
        }
    };

    const handleReturnToDashboard = async () => {
        try {
            await BLResetCurrentDynamic();
            getOutOfTheDynamic();
        } catch (error) { }
    };

    // ==========================================================================
    // RENDERIZADO
    // ==========================================================================
    return (
        <SecondaryBase actionToExecute={handleReturnToDashboard}>
            <section className='AdmRunToGoal__Settings'>
                <p className='AdmRunToGoal__Title'>Vas o No Vas (Maletines)</p>

                <div className='AdmRunToGoal__Settings__Controls'>
                    {/* ENCABEZADO DE STATUS (Idéntico a RunToGoal) */}
                    <div className='AdmRunToGoal__Text--Center border-bottom pb-3 mb-4'>
                        <div className="d-flex justify-content-between align-items-center px-3">
                            <div>
                                <p className="mb-1 text-muted small">Estatus Servidor</p>
                                <p className="fw-bold mb-0">
                                    {matchPhase === 'WAITING' && <span className="text-secondary"><i className="bi bi-gear-fill"></i> Configuración</span>}
                                    {matchPhase === 'SELECTING_MAIN' && <span className="text-warning"><i className="bi bi-person-bounding-box"></i> Esperando Selección de Jugador</span>}
                                    {matchPhase === 'OPENING_ROUNDS' && <span className="text-primary"><i className="bi bi-briefcase-fill"></i> Ronda de Apertura</span>}
                                    {matchPhase === 'SUSPENSE' && <span className="text-danger"><i className="bi bi-eye-fill"></i> ¡Suspenso en Pantalla!</span>}
                                    {matchPhase === 'REVEAL_MODAL' && <span className="text-success"><i className="bi bi-stars"></i> Revelando Premio</span>}
                                    {matchPhase === 'FINISHED' && <span className="text-dark"><i className="bi bi-flag-fill"></i> Juego Terminado</span>}
                                </p>
                            </div>

                            <div className="bg-dark text-white rounded px-4 py-2 shadow-sm text-center">
                                <p className="mb-0 small text-uppercase" style={{ letterSpacing: '1px', color: '#aaa' }}>Maletines Abiertos</p>
                                <h3 className="mb-0 fw-bold" style={{ fontFamily: 'monospace' }}>
                                    {totalOpenedBriefcases} / {totalBriefcasesQuantity}
                                </h3>
                            </div>
                        </div>
                    </div>

                    {/* ZONA DE CONFIGURACIÓN (Solo editable en espera) */}
                    {matchPhase === 'WAITING' && (
                        <div className="mb-4">
                            <label className='AdmRunToGoal__Settings__Controls__Labels AdmRunToGoal__SpaceUp'>
                                Cantidad total de Maletines:
                            </label>
                            <input
                                className='AdmRunToGoal__Settings__Controls__Inputs mb-3'
                                type='number'
                                min={2} max={30}
                                value={totalBriefcasesQuantity}
                                onChange={e => setTotalBriefcasesQuantity(+e.target.value || 9)}
                                disabled={isSystemWorking}
                            />

                            <label className='AdmRunToGoal__Settings__Controls__Labels'>
                                Duración del Suspenso antes de abrir (Milisegundos):
                            </label>
                            <input
                                className='AdmRunToGoal__Settings__Controls__Inputs mb-3'
                                type='number'
                                min={0}
                                value={suspenseDurationMilliseconds}
                                onChange={e => setSuspenseDurationMilliseconds(+e.target.value || 0)}
                                disabled={isSystemWorking}
                            />

                            <label className='AdmRunToGoal__Settings__Controls__Labels'>
                                Modo de Juego:
                            </label>
                            <select 
                                className='AdmRunToGoal__Settings__Controls__Inputs mb-3'
                                value={selectedGameMode}
                                onChange={e => setSelectedGameMode(Number(e.target.value))}
                                disabled={isSystemWorking}
                            >
                                <option value={0}>Modo 0: Aleatorio (Suerte real)</option>
                                <option value={1}>Modo 1: Un premio mayor garantizado en intento exacto</option>
                                <option value={2}>Modo 2: Lista de premios secuenciales (Con premio mayor)</option>
                            </select>

                            {selectedGameMode > 0 && (
                                <>
                                    <label className='AdmRunToGoal__Settings__Controls__Labels'>
                                        Intento ganador (en qué turno sale el premio mayor):
                                    </label>
                                    <input
                                        className='AdmRunToGoal__Settings__Controls__Inputs mb-3'
                                        type='number'
                                        min={1} max={totalBriefcasesQuantity}
                                        value={winningAttemptNumber}
                                        onChange={e => setWinningAttemptNumber(+e.target.value || 1)}
                                        disabled={isSystemWorking}
                                    />

                                    <label className='AdmRunToGoal__Settings__Controls__Labels'>
                                        Nombre del premio mayor:
                                    </label>
                                    <input
                                        className='AdmRunToGoal__Settings__Controls__Inputs mb-3'
                                        type='text'
                                        value={specialPrizeText}
                                        onChange={e => setSpecialPrizeText(e.target.value)}
                                        disabled={isSystemWorking}
                                    />
                                </>
                            )}

                            {selectedGameMode === 2 && (
                                <>
                                    <label className='AdmRunToGoal__Settings__Controls__Labels text-danger fw-bold'>
                                        Lista de premios secuenciales (Separados por coma):
                                    </label>
                                    <p className="text-muted small mb-1">Si dejas un espacio vacío entre comas, se mostrará "Sigue participando". El premio mayor configurado arriba sustituirá automáticamente la posición del 'Intento Ganador'.</p>
                                    <textarea
                                        className='AdmRunToGoal__Settings__Controls__Inputs mb-3'
                                        rows="3"
                                        value={prizesListString}
                                        onChange={e => setPrizesListString(e.target.value)}
                                        disabled={isSystemWorking}
                                    />
                                </>
                            )}
                        </div>
                    )}

                    {/* BOTONERA TÁCTICA (Cambia según la fase) */}
                    <div className="mt-4">
                        {matchPhase === 'WAITING' && (
                            <>
                                <button
                                    className={`btn btn-info fw-bold w-100 mb-3 shadow-sm ${isSystemWorking ? 'disabled' : ''}`}
                                    onClick={handleShowFrontPage}
                                    disabled={isSystemWorking}
                                >
                                    <i className="bi bi-display"></i> Mostrar portada animada
                                </button>

                                <button
                                    className={`Btn__Adsl--RedOutlineTransparent AdmRunToGoal__Settings__Controls__Buttons w-100 ${isSystemWorking ? 'disabled' : ''}`}
                                    onClick={handleStartGame}
                                    disabled={isSystemWorking}
                                >
                                    <i className="bi bi-play-circle-fill"></i> {isSystemWorking ? 'Iniciando...' : ' Iniciar Dinámica'}
                                </button>
                            </>
                        )}

                        {/* PANEL DE CONTROL EN VIVO (Visible cuando ya empezó) */}
                        {matchPhase !== 'WAITING' && (
                            <div className="bg-light p-3 rounded border mb-4">
                                <h5 className="fw-bold mb-3">Panel del operador</h5>
                                
                                {/* {userSelectedBriefcaseId && (
                                    <div className="alert alert-warning text-center fw-bold fs-5 shadow-sm border-warning">
                                        <i className="bi bi-person-fill"></i> El Aficionado eligió el maletín: #{userSelectedBriefcaseId}
                                    </div>
                                )} */}

                                {userSelectedBriefcaseId && (
                                    <div className="alert alert-danger text-center fw-bold fs-6 shadow-sm mb-2">
                                        <i className="bi bi-person-fill"></i> Maletín del aficionado (Intocable): #{userSelectedBriefcaseId}
                                    </div>
                                )}

                                {/* ALERTA CORREGIDA: Nos dice qué maletín eligió abrir */}
                                {matchPhase === 'OPENING_ROUNDS' && (
                                    <div className={`alert ${targetedBriefcaseId ? 'alert-info' : 'alert-secondary'} text-center fw-bold fs-5 shadow-sm`}>
                                        <i className="bi bi-cursor-fill me-2"></i> 
                                        {targetedBriefcaseId 
                                            ? `El aficionado PIDE ABRIR el maletín: #${targetedBriefcaseId}` 
                                            : "Esperando a que el aficionado marque un maletín..."}
                                    </div>
                                )}

                                <p className="text-muted small mb-2 fw-bold text-center">Maletines disponibles para abrir:</p>

                                <div className="d-flex flex-wrap gap-2 justify-content-center mb-3">
                                    {liveBriefcasesState.map(briefcaseItem => {
                                        let btnClass = 'btn-outline-primary';
                                        let isButtonDisabled = isSystemWorking || matchPhase === 'SUSPENSE' || matchPhase === 'REVEAL_MODAL';

                                        if (briefcaseItem.state === 'opened') {
                                            btnClass = 'btn-secondary opacity-50';
                                            isButtonDisabled = true;
                                        } else if (briefcaseItem.state === 'selected' || briefcaseItem.id === userSelectedBriefcaseId) {
                                            // ROJO PARA EL DEL AFICIONADO
                                            btnClass = 'btn-danger border-danger border-2 text-white'; 
                                        } else if (briefcaseItem.id === targetedBriefcaseId) {
                                            // CYAN Y PARPADEANTE PARA EL QUE PIDIÓ ABRIR
                                            btnClass = 'btn-info text-dark border-info border-2 shadow-lg placeholder-glow'; 
                                        }

                                        return (
                                            <button 
                                                key={briefcaseItem.id}
                                                className={`btn ${btnClass} fw-bold shadow-sm`}
                                                style={{ width: '60px', height: '60px', fontSize: '1.3rem' }}
                                                disabled={isButtonDisabled}
                                                onClick={() => handleOpenSpecificBriefcase(briefcaseItem.id)}
                                                title={briefcaseItem.id === userSelectedBriefcaseId ? "Maletín del Aficionado" : `Abrir maletín ${briefcaseItem.id}`}
                                            >
                                                {briefcaseItem.id}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* NUEVO: BOTÓN PARA CERRAR EL MODAL Y SEGUIR JUGANDO */}
                        {/* {matchPhase === 'REVEAL_MODAL' && (
                            <button 
                                className={`btn btn-success btn-lg fw-bold w-100 mb-3 shadow-sm ${isSystemWorking ? 'disabled' : ''}`}
                                onClick={handleContinueGame}
                                disabled={isSystemWorking}
                            >
                                <i className="bi bi-arrow-right-circle-fill me-2"></i> Ir a abrir otro maletín
                            </button>
                        )} */}

                        {/* NUEVO: BOTÓN PARA CERRAR EL MODAL Y SEGUIR JUGANDO O FINALIZAR */}
                        {matchPhase === 'REVEAL_MODAL' && (
                            <button 
                                className={`btn btn-success btn-lg fw-bold w-100 mb-3 shadow-sm ${isSystemWorking ? 'disabled' : ''}`}
                                onClick={handleContinueGame}
                                disabled={isSystemWorking}
                            >
                                <i className="bi bi-arrow-right-circle-fill me-2"></i> 
                                {totalOpenedBriefcases >= (totalBriefcasesQuantity - 1) ? 'Cerrar premio y finalizar juego' : 'Ir a abrir otro maletín'}
                            </button>
                        )}

                        {/* BOTÓN PELIGRO: ABORTAR */}
                        {matchPhase !== 'WAITING' && (
                            <button
                                className={`btn btn-outline-danger fw-bold w-100 ${isSystemWorking ? 'disabled' : ''}`}
                                onClick={handleAbortAndResetGame}
                                disabled={isSystemWorking}
                            >
                                <i className="bi bi-x-octagon-fill"></i> {matchPhase === 'FINISHED' ? 'Limpiar y volver a espera' : 'Abortar y reiniciar dinámica'}
                            </button>
                        )}
                    </div>

                    {errorMessage && (
                        <div className="alert alert-danger mt-3 shadow-sm border-0">
                            <strong>{errorMessage}</strong>
                        </div>
                    )}
                    {successMessage && (
                        <div className="alert alert-success mt-3 shadow-sm border-0">
                            <strong>{successMessage}</strong>
                        </div>
                    )}
                </div>
            </section>
        </SecondaryBase>
    );
};

export { Briefcase };